import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { shouldEndSessionOnAuthError } from '../lib/authSession';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true); // Start with loading=true

  // Validate token on app initialization
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        setLoading(false);
        return;
      }
      
      try {
        // First set user from localStorage immediately for faster UX
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setLoading(false);
        
        // Then validate token with backend in background
        const response = await authAPI.getMe();
        const validatedUser = response.data;
        
        // Update user state with fresh data if validation succeeds
        setUser(validatedUser);
      } catch (error) {
        if (shouldEndSessionOnAuthError(error)) {
          // The server says the token is not valid any more.
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          // 502 during a deploy, a timeout, 429: keep the session and the
          // cached user; the next request goes through when the server is back.
          console.warn('Session check failed temporarily, keeping the session', error?.response?.status || error?.message);
        }
        setLoading(false);
      }
    };
    
    validateToken();
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authAPI.login({ email, password });
      
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setUser(user);
      setLoading(false);
      
      return user;
    } catch (error) {
      console.error('❌ Login failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);
      const { access_token, user } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      setLoading(false);
      
      return user;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  // OAuth login - called from AuthCallbackPage
  const setUserFromOAuth = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('just_authenticated');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUserFromOAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
